'use client'

import Link from '@/components/Link/Link'
import styles from './Navigation.module.scss'
import useStore from '@/store'
import { useContext, useEffect, useRef, useState } from 'react'
import { GlobalSettingsContext } from '@/context/GlobalSettings'
import { DOC_TYPES, STICKY_BANNER_CLOSED_COOKIE_NAME } from '@/data'
import HeaderLogo from '@/components/_svgs/HeaderLogo'
import NavigationButtons from '@/components/Navigation/NavigationButtons/NavigationButtons'
import classNames from 'classnames'
import useCurrentPage from '@/hooks/use-current-page'
import Icon from '@/components/Icon/Icon'
import NavigationMobileDropdown from '@/components/Navigation/NavigationMobileDropdown/NavigationMobileDropdown'
import useBreakpoint from '@/hooks/use-breakpoint'
import useI18n from '@/hooks/use-i18n'
import StickyBanner from '@/components/StickyBanner/StickyBanner'
import { shouldShowStickyBannerOnPage } from '@/components/StickyBanner/shouldShowStickyBannerOnPage'
import { getCookie } from '@/utils/storage'
import Image from 'next/image'

const TRANSPARENT_BG_SECTION_TYPES = ['homepageHero', 'homepageHeroLNY', 'mediaBackgroundAndTitle']
const OPAQUE_BG_SECTION_TYPES_MOBILE = ['mediaBackgroundAndTitle'] as string[]
const HIDE_LOGO_AND_BUTTONS_TYPES = ['homepageHero', 'homepageHeroLNY']

// Hard-coded list of page slugs that need a light (black text) nav theme
// to maintain contrast against dark hero backgrounds. Making this CMS-driven
// is out of scope; update this array when adding new pages with dark heroes.
const LIGHT_NAV_THEME_PAGES = ['holiday-season']

export const NAV_ID = 'nav'

const Navigation = () => {
  const navTheme = useStore(state => state.navTheme)
  const navScrollingOverSection = useStore(state => state.navScrollingOverSection)
  const [currentNavTheme, setCurrentNavTheme] = useState<SectionBackground>(navTheme)
  const [currentScrollingOverSection, setCurrentScrollingOverSection] = useState<string | null>(null)
  const navThemeDebounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { navigationData } = useContext(GlobalSettingsContext)
  const { currentSlug } = useCurrentPage()
  const navIsOpen = useStore(state => state.navIsOpen)
  const setNavIsOpen = useStore(state => state.setNavIsOpen)
  const { breakpoint } = useBreakpoint()
  const isLoading = breakpoint === null || navScrollingOverSection === null
  const { i18n } = useI18n()
  const { isMobile } = useBreakpoint()
  const isOpaqueMobileBg = isMobile && OPAQUE_BG_SECTION_TYPES_MOBILE.includes(currentScrollingOverSection as string)
  const stickyBannerIsOpen = useStore(state => state.stickyBannerIsOpen)
  const setStickyBannerIsOpen = useStore(state => state.setStickyBannerIsOpen)
  const bannerShowsOnThisPage = shouldShowStickyBannerOnPage(navigationData?.stickyBanner, currentSlug)
  const [isAtTop, setIsAtTop] = useState(true)

  const checkScrollPosition = () => {
    const isCurrentlyAtTop = window.scrollY === 0
    setIsAtTop(isCurrentlyAtTop)
  }

  useEffect(() => {
    checkScrollPosition()

    // Add scroll listener
    window.addEventListener('scroll', checkScrollPosition)

    // Cleanup
    return () => {
      window.removeEventListener('scroll', checkScrollPosition)
    }
  }, [])

  useEffect(() => {
    if (breakpoint?.name === 'desktop' || breakpoint?.name === 'xl') {
      setNavIsOpen(false)
    }
  }, [setNavIsOpen, breakpoint])

  useEffect(() => {
    if (navThemeDebounceTimeout.current) {
      clearTimeout(navThemeDebounceTimeout.current)
    }

    navThemeDebounceTimeout.current = setTimeout(() => {
      // Override nav theme for specific pages with dark hero backgrounds (see LIGHT_NAV_THEME_PAGES)
      const shouldUseLightTheme =
        LIGHT_NAV_THEME_PAGES.includes(currentSlug as string) &&
        TRANSPARENT_BG_SECTION_TYPES.includes(navScrollingOverSection as string)

      setCurrentNavTheme(shouldUseLightTheme ? 'black' : navTheme)
      setCurrentScrollingOverSection(navScrollingOverSection)
    }, 50)
  }, [navTheme, navScrollingOverSection, currentSlug])

  useEffect(() => {
    // The banner only occupies layout space when it is enabled in the CMS, scoped to this page,
    // and not dismissed via cookie. All three have to be checked here: <StickyBanner> only mounts
    // once this flag is already true, so it cannot correct the flag on pages it does not render
    // on. Miss the page scoping and every consumer (nav offset, body data attribute, the mobile
    // sticky category bar) adds a phantom banner-height offset on every other page.
    const notDismissed = !(getCookie(STICKY_BANNER_CLOSED_COOKIE_NAME) === 'true')
    setStickyBannerIsOpen(notDismissed && bannerShowsOnThisPage)
  }, [setStickyBannerIsOpen, bannerShowsOnThisPage])

  useEffect(() => {
    document.body.dataset.stickyBannerOpen = String(
      stickyBannerIsOpen && !!navigationData?.stickyBanner?.isEnabled,
    )
  }, [stickyBannerIsOpen, navigationData?.stickyBanner?.isEnabled])

  if (!navigationData?.links?.length) return null

  return (
    <>
      <header
        id={NAV_ID}
        className={classNames(
          styles.Navigation,
          {
            [styles.isGlassyBg]:
              TRANSPARENT_BG_SECTION_TYPES.includes(currentScrollingOverSection as string) &&
              !isOpaqueMobileBg &&
              !(isMobile && navIsOpen && currentScrollingOverSection === 'homepageHero'),
          },
          {
            [styles.isTransparentBg]: isAtTop && !navIsOpen,
          },
          {
            [styles.navIsOpen]: navIsOpen,
          },
          {
            [styles.hideLogoAndButtons]: HIDE_LOGO_AND_BUTTONS_TYPES.includes(currentScrollingOverSection as string),
          },
          {
            [styles.loading]: isLoading,
          },
        )}
        data-scrolling-over-section={currentScrollingOverSection}
        data-theme={currentNavTheme}
      >
        {stickyBannerIsOpen && navigationData?.stickyBanner && <StickyBanner data={navigationData.stickyBanner} />}
        <nav className={styles.nav}>
          <Link
            link={{
              linkType: 'internal',
              link: {
                _type: DOC_TYPES.PAGE,
                slug: '',
              },
            }}
            className={styles.logoContainer}
            onClick={() => {
              setNavIsOpen(false)
            }}
            ariaLabel={i18n('ariaGoHome')}
          >
            {process.env.NEXT_PUBLIC_IS_CANADA_WEBSITE === 'true' ? (
              <>
                <Image
                  src={
                    currentNavTheme === 'black'
                      ? '/images/canadian-logo/canadian-logo-horizontal-white.png'
                      : '/images/canadian-logo/canadian-logo-horizontal-black.png'
                  }
                  alt="DTF Canada"
                  className={classNames(styles.logoMobile, styles.canadaLogoMobile)}
                  width={814}
                  height={60}
                  priority
                />
                <Image
                  src={
                    currentNavTheme === 'black'
                      ? '/images/canadian-logo/canadian-logo-horizontal-white.png'
                      : '/images/canadian-logo/canadian-logo-horizontal-black.png'
                  }
                  alt="DTF Canada"
                  className={classNames(styles.logoDesktop, styles.canadaLogoDesktop)}
                  width={143}
                  height={40}
                  priority
                />
              </>
            ) : (
              <>
                <Icon
                  name="footerSymbol"
                  className={styles.logoMobile}
                />
                <HeaderLogo className={styles.logoDesktop} />
              </>
            )}
          </Link>
          <ul className={styles.desktopLinkList}>
            {navigationData.links.map((link, i) => {
              const internalLink = link.link as SanityLinkInternal
              return (
                <li
                  className={classNames(styles.desktopLinkList__item, {
                    [styles.isActive]: currentSlug === internalLink?.slug,
                  })}
                  key={i}
                >
                  <Link
                    link={link}
                    className={styles.desktopLinkList__item__link}
                  />
                </li>
              )
            })}
          </ul>
          <div className={styles.buttons}>
            <div className={styles.buttons__desktop}>
              <NavigationButtons currentScrollingOverSection={currentScrollingOverSection || ''} />
            </div>
            <button
              className={styles.mobileToggleButton}
              onClick={() => {
                setNavIsOpen(!navIsOpen)
              }}
              aria-label={i18n(navIsOpen ? 'ariaCloseNavigation' : 'ariaOpenNavigation')}
            >
              <span className={styles.mobileToggleButton__hamburgerContainer}>
                <Icon name="hamburger" />
              </span>
              <span className={styles.mobileToggleButton__closeContainer}>
                <Icon name="close" />
              </span>
            </button>
          </div>
        </nav>
      </header>
      <NavigationMobileDropdown
        scrollingOverSection={currentScrollingOverSection}
        navTheme={currentNavTheme}
      />
      <div
        className={classNames(
          {
            [styles.isGlassyBg]:
              TRANSPARENT_BG_SECTION_TYPES.includes(currentScrollingOverSection as string) &&
              !isOpaqueMobileBg &&
              !(isMobile && navIsOpen && currentScrollingOverSection === 'homepageHero'),
          },
          {
            [styles.isTransparentBg]: isAtTop && !navIsOpen,
          },
          {
            [styles.navIsOpen]: navIsOpen && !stickyBannerIsOpen,
          },
          {
            [styles.loading]: isLoading,
          },
          styles.headerBg,
          {
            [styles.stickyIsOpen]: !navIsOpen && stickyBannerIsOpen && navigationData.stickyBanner?.isEnabled,
          },
          {
            [styles.stickyIsOpenAndNavIsOpen]:
              stickyBannerIsOpen && navIsOpen && navigationData.stickyBanner?.isEnabled,
          },
        )}
        data-scrolling-over-section={currentScrollingOverSection}
        data-theme={currentNavTheme}
      />
    </>
  )
}

Navigation.displayName = 'Navigation'

export default Navigation
